/**
 * Questionnaire answers waiting for the account they belong to.
 *
 * The questionnaire is answered before an account exists, so its answers wait
 * here in localStorage (it survives closing the tab) bound to the email typed on
 * the account step, and are sent in ONE PUT /user/profile the first time that
 * exact email (trimmed, case kept: the user pool treats Bob@x.com and bob@x.com
 * as different accounts) is signed in, then retried on each page load until it
 * succeeds. A 400 can never succeed, so it discards the copy; any other failure
 * keeps it. A different account signing in leaves it untouched, unsent. Any page
 * load, signed in or not, deletes a copy older than 7 days, and a deliberate
 * sign-out or account deletion deletes the copy only if it is that account's
 * own. When sign-up says the account already exists, the copy is kept on
 * purpose: that existing account's next sign-in on this browser saves these
 * answers into it, replacing what it held.
 *
 * The profile holds a legal name and nationality. Never put its content in a
 * log line — status codes and messages only.
 */

import { updateUserProfile } from '@/services/api/user';
import { ApiClientError } from '@/services/api/client';
import type { IdentitySpine, QuestionnaireAnswers } from '@/types/api';

export const PENDING_PROFILE_KEY = 'socialyse.pendingProfile.v1';
export const PENDING_PROFILE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** The flat PUT /user/profile body: the identity spine plus the answers. */
export type PendingProfileBody = IdentitySpine & QuestionnaireAnswers;

interface StoredPendingProfile {
  email: string;
  savedAt: string;
  profile: PendingProfileBody;
}

const LOG_PREFIX = '[pendingProfile]';

/**
 * Trimmed, case kept. The user pool is case-sensitive (no
 * UsernameConfiguration in 06-api.yaml), so Bob@x.com and bob@x.com can be two
 * accounts, and Cognito returns the email exactly as it was signed up. Exact
 * matching can at worst leave a copy unsent, never send it to the wrong account.
 */
function sameEmail(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.trim() === b.trim();
}

/** Drop empty strings, blank list entries and empty lists; trim the rest. */
function compactProfile(profile: PendingProfileBody): PendingProfileBody {
  const out: Record<string, string | string[]> = {};
  for (const [key, value] of Object.entries(profile)) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) out[key] = trimmed;
    } else if (Array.isArray(value)) {
      const items = value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter(Boolean);
      if (items.length > 0) out[key] = items;
    }
  }
  return out as PendingProfileBody;
}

function removePending(): void {
  try {
    localStorage.removeItem(PENDING_PROFILE_KEY);
  } catch {
    // Storage unavailable (private browsing). Nothing more can be done; a copy
    // that could not be removed is re-sent next time, and the PUT is idempotent.
  }
}

function readPending(): StoredPendingProfile | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(PENDING_PROFILE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredPendingProfile> | null;
    if (
      parsed &&
      typeof parsed.email === 'string' &&
      typeof parsed.savedAt === 'string' &&
      parsed.profile &&
      typeof parsed.profile === 'object'
    ) {
      return parsed as StoredPendingProfile;
    }
  } catch {
    // Fall through: unreadable, so it can never be sent.
  }
  removePending();
  return null;
}

function isStale(pending: StoredPendingProfile): boolean {
  const savedAt = Date.parse(pending.savedAt);
  return !Number.isFinite(savedAt) || Date.now() - savedAt > PENDING_PROFILE_MAX_AGE_MS;
}

/**
 * Delete the copy if it is unreadable, undated or older than 7 days. Called on
 * every page load, signed in or not, so a legal name and nationality never
 * linger on the device past a week. Never throws.
 */
export function discardStalePendingProfile(): void {
  const pending = readPending(); // removes an unreadable copy itself
  if (pending && isStale(pending)) removePending();
}

/**
 * Delete the copy if it was typed for `email`: the account signing out (or
 * being deleted) takes its own leftover answers with it. Someone else's copy on
 * this browser is not theirs to delete, so it stays. Never throws.
 */
export function clearPendingProfileFor(email: string | null | undefined): void {
  const pending = readPending();
  if (pending && sameEmail(email, pending.email)) removePending();
}

/**
 * Keep the answers on this device until `email`'s owner signs in.
 * Never throws: if storage is unavailable the answers are simply not kept.
 */
export function storePendingProfile(email: string, profile: PendingProfileBody): void {
  const record: StoredPendingProfile = {
    email: email.trim(),
    savedAt: new Date().toISOString(),
    profile: compactProfile(profile),
  };
  try {
    localStorage.setItem(PENDING_PROFILE_KEY, JSON.stringify(record));
  } catch {
    console.warn(`${LOG_PREFIX} Could not keep the answers on this device (storage unavailable).`);
  }
}

async function sendPending(signedInEmail: string | null | undefined): Promise<void> {
  const pending = readPending();
  if (!pending) return;

  if (isStale(pending)) {
    removePending();
    return;
  }

  // A different account (or an unknown one) is signed in: the copy belongs to
  // someone else on this browser, so leave it, unsent, for its owner. The
  // 7-day purge bounds how long it can wait.
  if (!sameEmail(signedInEmail, pending.email)) return;

  try {
    await updateUserProfile(pending.profile);
    removePending();
  } catch (err) {
    if (err instanceof ApiClientError && err.statusCode === 400) {
      // The server says the body is invalid; sending it again cannot succeed.
      console.warn(`${LOG_PREFIX} Saved answers were rejected (400): ${err.message}. Discarding them.`);
      removePending();
      return;
    }
    const status = err instanceof ApiClientError ? err.statusCode : 'no response';
    console.warn(`${LOG_PREFIX} Could not save answers yet (${status}); will retry at the next sign-in or page load.`);
  }
}

let inFlight: Promise<void> | null = null;

/**
 * Send the waiting answers if they were typed for exactly `signedInEmail`.
 *
 * Success, or a 400 (which can never succeed), removes the copy; any other
 * failure, or a different account, keeps it for the next sign-in or page load.
 * Never throws. Two calls at once share one request.
 */
export function flushPendingProfile(signedInEmail: string | null | undefined): Promise<void> {
  if (!inFlight) {
    inFlight = sendPending(signedInEmail)
      .catch(() => {
        // sendPending handles its own errors; this only guards the promise.
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}
